import React, { ReactElement } from 'react'
import { camelCase, kebabCase } from 'lodash'

import EntryMarker from 'marketing-site/src/components/common/EntryMarker'
import {
  IPreFooterCtaBlock as IContentfulPreFooterCtaBlock,
  ICtaLink as IContentfulCTALink,
  ICtaButtonMvp as IContentfulCTAButtonMVP,
  ICtaLinkMvp as IContentfulCTALinkMVP,
  ISignupCta as IContentfulSignUpCTA,
  ITwoCtAsHeroElement as IContentfulTwoCTAsHeroElement,
} from 'marketing-site/@types/generated/contentful'
import {
  PreFooterCTABlock,
  IProps as IPreFooterCTABlock,
} from 'marketing-site/src/mvp/library/components/PreFooterCTABlock'
import {
  transformCTALink,
  transformCTAWithBackground,
} from 'marketing-site/src/mvp/transformers/elements/ContentfulCTALink'
import { CTALink } from 'marketing-site/src/mvp/library/elements/CTALink'
import { CTAButton } from 'marketing-site/src/mvp/library/elements/CTAButton'
import { SignUpCTA } from 'marketing-site/src/mvp/library/elements/SignUpCTA'
import { transformCTAButton } from '../elements/ContentfulCTAButton'
import { transformSignupCTA } from 'marketing-site/src/transformers/elements/ContentfulSignUpCTA'
import { transformCtaHeroElement } from 'marketing-site/src/transformers/components/ContentfulHeroWithCTA'
import { CTA } from 'marketing-site/src/transformers/CTA'
import { mq } from 'marketing-site/src/library/utils'
import { Entry } from 'contentful'

export const ContentfulPreFooterCTABlock = (
  preFooterCTABlock: IContentfulPreFooterCtaBlock,
): React.ReactElement => (
  <EntryMarker entry={preFooterCTABlock}>
    {() => <PreFooterCTABlock {...transformPreFooterCTABlock(preFooterCTABlock)} />}
  </EntryMarker>
)

export function ctaContentType(entry: Entry<any>): string {
  return entry.sys.contentType!.sys.id
}

interface ITwoCTAs {
  renderCta1: () => React.ReactNode
  renderCta2?: () => React.ReactNode
}

export const TwoCTAs = (twoCTAs: ITwoCTAs) => (
  <div className="pre-footer__ctas-wrapper">
    {twoCTAs.renderCta1()}
    {twoCTAs.renderCta2 && twoCTAs.renderCta2()}
    <style jsx>{`
      .pre-footer__ctas-wrapper {
        display: flex;
        justify-content: center;
        align-items: stretch;
        flex-direction: column;

        :global(& > *:not(:first-child)) {
          margin-top: 0.5rem;
        }

        @media (${mq.tablet}) {
          align-items: center;
          flex-direction: row;
          :global(& > *:not(:first-child)) {
            margin-left: 0.75rem;
            margin-top: 0;
          }
        }
      }
    `}</style>
  </div>
)

const transformCTA = (
  cta: IContentfulCTAButtonMVP | IContentfulCTALinkMVP | IContentfulSignUpCTA,
): ReactElement => {
  switch (cta.sys.contentType.sys.id) {
    case 'ctaButtonMvp':
      return <CTAButton {...transformCTAButton(cta as IContentfulCTAButtonMVP)} />
    case 'ctaLinkMvp':
      return <CTALink {...transformCTALink(cta as IContentfulCTALinkMVP)} />
    case 'signupCta':
      return <SignUpCTA {...transformSignupCTA(cta as IContentfulSignUpCTA)} />
  }
}

const transformSignUpCTA = (
  signUpCTA: IContentfulCTALink | IContentfulSignUpCTA | IContentfulTwoCTAsHeroElement,
) => {
  switch (signUpCTA.sys.contentType.sys.id) {
    case 'twoCtAsHeroElement':
      const twoCTAs = transformCtaHeroElement(signUpCTA as IContentfulTwoCTAsHeroElement)
      return () => <TwoCTAs {...twoCTAs} />
    default:
      return () => <CTA {...(signUpCTA as IContentfulCTALink | IContentfulSignUpCTA)} />
  }
}

export function transformPreFooterCTABlock({
  fields,
}: IContentfulPreFooterCtaBlock): IPreFooterCTABlock {
  const ctaProps: IContentfulCTALink | IContentfulSignUpCTA | IContentfulTwoCTAsHeroElement =
    fields.cta
  return {
    ...fields,
    width: fields.width && (kebabCase(fields.width) as IPreFooterCTABlock['width']),
    linkText: fields.linkText && transformCTAWithBackground(fields.linkText),
    cta: fields.cta && transformSignUpCTA(fields.cta),
    ctaContentType: ctaContentType(ctaProps) as 'ctaLink' | 'signupCta' | 'twoCtAsHeroElement',
    ctas: fields.ctas.map((cta) => transformCTA(cta)),
    backgroundColor:
      fields.backgroundColor &&
      (camelCase(fields.backgroundColor) as IPreFooterCTABlock['backgroundColor']),
  }
}
